import type { ChassisCodeEntry } from "@bt/parts-core";
import type { ChassisCodeColor, ChassisCodeListStyle } from "../chassis-codes";

// How the Munich data package's chassis codes are coloured; its lists are data in @bt/parts-core
// (data/make-packages/munich.json, ADR 0014).

/**
 * Built-in codes are coloured by model series, each series a family of similar hues that its
 * generations take turns with in production order, so neighbouring generations differ.
 */
const BMW_SERIES_HUES = {
  compact: ["cyan", "teal"],
  three: ["sky", "blue", "indigo"],
  five: ["emerald", "green"],
  seven: ["violet", "purple"],
  x: ["amber", "yellow"],
  other: ["slate", "stone"],
} as const satisfies Record<string, readonly ChassisCodeColor[]>;

function bmwSeries(entry: ChassisCodeEntry): keyof typeof BMW_SERIES_HUES {
  const model = entry.model.en;
  if (/^[12] Series/.test(model)) return "compact";
  if (/^[34] Series|^325iX/.test(model)) return "three";
  if (/^[56] Series/.test(model)) return "five";
  if (/^[78] Series/.test(model)) return "seven";
  if (/^X\d/.test(model)) return "x";
  return "other";
}

/** One series per list for BMW M, MINI and BMW Motorrad. */
function oneSeries(hues: readonly ChassisCodeColor[]): ChassisCodeListStyle {
  return { series: () => "all", hues: { all: hues } };
}

export const MUNICH_CHASSIS_CODE_STYLES: Record<
  "bmw" | "bmw_m" | "mini" | "motorrad",
  ChassisCodeListStyle
> = {
  bmw: { series: bmwSeries, hues: BMW_SERIES_HUES },
  bmw_m: oneSeries(["red", "rose"]),
  mini: oneSeries(["pink", "fuchsia"]),
  motorrad: oneSeries(["orange"]),
};
